import React, { useState } from "react";
import { Play, Tv, Mic2, Users, X } from "lucide-react";

const LIVE_IMG =
  "https://customer-assets.emergentagent.com/job_schlager-pro-daniel/artifacts/i2a66469_IMG_3383.jpeg";
const PRESS_IMG =
  "https://customer-assets.emergentagent.com/job_schlager-pro-daniel/artifacts/q8n9erjz_IMG_8638.jpeg";

const LIVE_VIDEO_ID = "spazz4wqZhk"; // Sex on Fire — Live Cover
const LIVE_VIDEO_URL = `https://www.youtube.com/watch?v=${LIVE_VIDEO_ID}`;

export default function MediaShowcase() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <section
      id="live"
      data-testid="media-section"
      className="relative bg-[#0A0A0A] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="overline" data-testid="media-overline">
              <span className="gold-rule mr-4 align-middle" />
              Live · TV · Medien
            </p>
            <h2
              className="font-display mt-4 text-white leading-tight tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", fontWeight: 600 }}
              data-testid="media-headline"
            >
              Bühne. Kamera.
              <br />
              <span className="italic text-[#D4AF37]">Standing Ovations.</span>
            </h2>
          </div>
          <p className="text-[#A3A3A3] max-w-md font-body text-base leading-relaxed">
            Ob Festivalbühne, Late-Night-Sofa oder Reality-Format — Daniel
            Lopes ist ein Profi für jedes Format und jede Zielgruppe.
          </p>
        </div>

        {/* Bento Grid */}
        <div
          className="grid grid-cols-12 grid-rows-2 gap-4 md:gap-6 min-h-[640px] md:min-h-[760px]"
          data-testid="media-bento-grid"
        >
          {/* Big live image — clickable to open YouTube modal */}
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            className="col-span-12 md:col-span-8 row-span-2 relative zoom-wrap group cursor-pointer border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500 text-left p-0 bg-transparent"
            data-testid="media-live-tile"
            aria-label="Live-Video abspielen: Sex on Fire"
          >
            <img
              src={LIVE_IMG}
              alt="Live Performance Daniel Lopes"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
            <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A]/80 backdrop-blur-md border border-white/10">
              <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[10px] tracking-[0.28em] uppercase text-white">
                Live · Sex on Fire
              </span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-[#D4AF37] flex items-center justify-center bg-[#0A0A0A]/40 backdrop-blur-md group-hover:bg-[#D4AF37] transition-all duration-500">
                <Play
                  size={28}
                  strokeWidth={1.5}
                  className="text-[#D4AF37] group-hover:text-[#0A0A0A] transition-colors ml-1"
                />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <h3 className="font-display text-2xl md:text-4xl text-white leading-tight">
                „Sex on Fire" — <span className="italic text-[#D4AF37]">Live</span>
              </h3>
              <p className="mt-2 text-[#C9C9C9] font-body text-sm md:text-base max-w-md">
                Stimme, Energie, Bühnenpräsenz — der ganze Saal kocht.
                Jetzt das Live-Video ansehen.
              </p>
            </div>
          </button>

          {/* Press / Red carpet */}
          <div
            className="col-span-12 md:col-span-4 row-span-1 relative zoom-wrap group border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500"
            data-testid="media-press-tile"
          >
            <img
              src={PRESS_IMG}
              alt="Daniel Lopes Red Carpet TV-Event"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A]/80 backdrop-blur-md border border-white/10">
              <Tv size={12} strokeWidth={1.5} className="text-[#D4AF37]" />
              <span className="text-[10px] tracking-[0.28em] uppercase text-white">
                TV · Red Carpet
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <h3 className="font-display text-xl md:text-2xl text-white">
                Promi Big Brother
              </h3>
              <p className="mt-1 text-[#A3A3A3] text-xs tracking-[0.18em] uppercase">
                TV · 2024
              </p>
            </div>
          </div>

          {/* Stats tile */}
          <div
            className="col-span-12 md:col-span-4 row-span-1 bg-[#141414] p-6 md:p-8 flex flex-col justify-between border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500"
            data-testid="media-stats-tile"
          >
            <div className="flex items-center gap-2">
              <Mic2 size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#A3A3A3]">
                Repertoire
              </span>
            </div>
            <div>
              <div className="font-display text-5xl md:text-6xl text-[#D4AF37] leading-none">
                90<span className="text-2xl">min</span>
              </div>
              <p className="mt-3 text-[#C9C9C9] text-sm leading-relaxed">
                Headliner-Set + Zugaben. Voll inszeniert mit Band oder
                Playback-Setup.
              </p>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
              <Users size={14} strokeWidth={1.5} className="text-[#A3A3A3]" />
              <span className="text-xs tracking-[0.18em] uppercase text-[#A3A3A3]">
                500 – 50.000 Gäste
              </span>
            </div>
          </div>
        </div>

        {/* Booker logos / formats strip */}
        <div
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5"
          data-testid="media-formats"
        >
          {[
            { icon: Tv, label: "TV-Shows", sub: "RTL · SAT.1 · Joyn · Pro7" },
            { icon: Mic2, label: "Festivals", sub: "Schlager-Open-Airs DE/AT/CH" },
            { icon: Users, label: "Gala-Events", sub: "Firmenkunden · Premium" },
            { icon: Play, label: "Privat & Hochzeiten", sub: "Exklusive Acts" },
          ].map((f) => (
            <div
              key={f.label}
              data-testid={`format-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-[#0F0F0F] p-6 md:p-8 flex flex-col gap-3 hover:bg-[#141414] transition-colors duration-300"
            >
              <f.icon size={20} strokeWidth={1.5} className="text-[#D4AF37]" />
              <h4 className="font-display text-lg md:text-xl text-white">
                {f.label}
              </h4>
              <p className="text-xs tracking-[0.16em] uppercase text-[#A3A3A3]">
                {f.sub}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* YouTube Video Modal */}
      {videoOpen && (
        <div
          data-testid="media-video-modal"
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8 animate-in"
          onClick={() => setVideoOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setVideoOpen(false)}
            data-testid="media-video-close"
            aria-label="Video schließen"
            className="absolute top-6 right-6 md:top-8 md:right-8 w-11 h-11 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white flex items-center justify-center transition-colors"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl aspect-video bg-black border border-[#D4AF37]/30 relative"
          >
            <iframe
              data-testid="media-video-iframe"
              src={`https://www.youtube.com/embed/${LIVE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
              title="Daniel Lopes — Sex on Fire Live"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      )}
    </section>
  );
}
